import { EditorState } from 'draft-js';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import muiWithStyles from '@material-ui/core/styles/withStyles';
import {
  defineMessages,
  injectIntl,
  intlShape,
  FormattedMessage,
} from 'react-intl';
import {
  propTypes as reduxFormPropTypes,
  reduxForm,
  SubmissionError,
  Field,
} from 'redux-form';
import green from '@material-ui/core/colors/green';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Snackbar from '@material-ui/core/Snackbar';

import classNames from 'classnames';
import _ from 'lodash';

import RichEditor, {
  emptyContent,
  emptyContentState,
} from '../../RichEditor/RichEditor';

import validate from './validate';
import s from './PostDiscussionMessageForm.scss';

const messages = defineMessages({
  saveMsg: {
    id: 'PostDiscussionMessageForm.saveMsg',
    defaultMessage: 'Message has been posted',
    description:
      'Message displayed on post new discussion message form when it is saved successfully',
  },
  send: {
    id: 'PostDiscussionMessageForm.send',
    defaultMessage: 'Send',
    description: 'Button label on post new discussion message form',
  },
});

const styles = () => ({
  paper: {
    marginTop: -16,
  },
  fabProgress: {
    color: green[500],
    position: 'absolute',
    top: -6,
    left: -6,
    zIndex: 1,
  },
  buttonSuccess: {
    backgroundColor: green[500],
    '&:hover': {
      backgroundColor: green[700],
    },
  },
});

@injectIntl
@muiWithStyles(styles)
@withStyles(s)
@reduxForm({
  form: 'postDiscussion',
  validate,
})
export default class PostDiscussionMessageForm extends Component {
  static propTypes = {
    discussionId: PropTypes.string.isRequired,
    intl: intlShape.isRequired,
    onSave: PropTypes.func.isRequired,
    ...reduxFormPropTypes,
    classes: PropTypes.shape({}).isRequired,
  };

  static contextTypes = {
    fetch: PropTypes.func,
  };

  state = {
    isSubmitted: false,
    submitting: false,
  };

  richEditor = null;

  submit = data => {
    const text = _.assign({}, emptyContent, data.text);
    const processedData = {
      text,
    };

    return this.create(processedData);
  };

  create = data =>
    this.context
      .fetch(`/api/discussions/${this.props.discussionId}/messages`, {
        method: 'POST',
        body: JSON.stringify(data),
      })
      .then(res =>
        res.json().then(resJson => {
          if (!res.ok) {
            throw new SubmissionError(resJson);
          }
          return resJson;
        }),
      )
      .catch(err => {
        throw new SubmissionError(err && err.errors);
      })
      .then(message => {
        this.setState({
          isSubmitted: true,
        });
        const { reset, onSave } = this.props;
        onSave(message._id);

        if (this.richEditor) {
          this.richEditor.onChange(
            EditorState.createWithContent(emptyContentState),
          );
        }

        reset();
      });

  handleCloseSnackbar = () => {
    this.setState({ isSubmitted: false });
  };

  render() {
    const {
      classes,
      intl: { formatMessage },
      handleSubmit,
    } = this.props;
    const { isSubmitted, submitting } = this.state;

    return (
      <div className={classes.paper} id="post-discussion-message-form">
        <form noValidate autoComplete="off">
          <Field
            name="text"
            component={RichEditor}
            blockTypes={[{ label: 'Blockquote', style: 'blockquote' }]}
            ref={node => {
              this.richEditor = node && node.getRenderedComponent();
            }}
            forwardRef
          />
          <div className={s.saveBtnWrapper}>
            <Button
              data-test-id="W7plzC08BPWtwrOGkPpa9"
              color="primary"
              variant="contained"
              type="submit"
              onClick={handleSubmit(this.submit)}
              className={classNames({
                [classes.buttonSuccess]: isSubmitted,
                [s.saveBtn]: true,
              })}
              disabled={submitting}
              id="post-discussion-message-form--button"
            >
              <FormattedMessage {...messages.send} />
            </Button>
            {submitting && (
              <CircularProgress size={52} className={classes.fabProgress} />
            )}
          </div>
        </form>
        <Snackbar
          open={isSubmitted}
          onClose={this.handleCloseSnackbar}
          autoHideDuration={3000}
          message={formatMessage(messages.saveMsg)}
        />
      </div>
    );
  }
}
